import React, { useState } from 'react';
import './Dropdown.scss';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  id: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  width?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  width = 'auto'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`dropdown ${className}`} style={{ width, maxWidth: '100%' }}>
      <button
        type="button"
        className={`dropdown__trigger ${isOpen ? 'dropdown__trigger--open' : ''} ${disabled ? 'dropdown__trigger--disabled' : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Select ${placeholder}`}
        title={displayValue}
      >
        <span className="dropdown__value">{displayValue}</span>
        <img 
          src="https://figma-alpha-api.s3.us-west-2.amazonaws.com/images/1523dd72-a740-4886-b9aa-2957946643de"
          alt=""
          className={`dropdown__chevron ${isOpen ? 'dropdown__chevron--open' : ''}`}
        />
      </button>
      
      {isOpen && (
        <ul 
          className="dropdown__menu"
          role="listbox"
          aria-label={`${placeholder} options`}
        >
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className={`dropdown__option ${value === option.value ? 'dropdown__option--selected' : ''}`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
